import { LocationRes } from '@/types/api/user-auth/location.d';

// 创建一个独立的 axios 实例，避免循环依赖
import axios from 'axios';
import { Env } from '@/libs/env';
import { IP_LOCATION_KEY } from '@/const/storage-key';
import { ERROR_CODE_IP } from '@/types/error-code';
import { findLocale } from '@/libs/find-locale';
interface CachedLocation {
  data: LocationRes;
  timestamp: number;
  expiresIn: number; // 缓存过期时间（毫秒）
}

class IpLocationManager {
  private cache: CachedLocation | null = null;
  private readonly CACHE_DURATION = 2 * 60 * 1000; // 2分钟缓存
  private readonly STORAGE_KEY = IP_LOCATION_KEY;
  private refreshPromise: Promise<LocationRes> | null = null; // 防止重复请求
  private readonly httpClient = axios.create({
    baseURL: `${Env.NEXT_PUBLIC_HTTP_SERVER}` || '/api',
    timeout: 10000,
    withCredentials: true,
  });

  constructor() {
    this.loadFromStorage();
  }

  /**
   * 从 sessionStorage 加载缓存
   */
  private loadFromStorage(): void {
    if (typeof window === 'undefined') return;

    try {
      const cached = sessionStorage.getItem(this.STORAGE_KEY);
      if (cached) {
        this.cache = JSON.parse(cached);
      }
    } catch (error) {
      console.warn('Failed to load IP location cache:', error);
      this.cache = null;
    }
  }

  /**
   * 保存到 sessionStorage
   */
  private saveToStorage(): void {
    if (typeof window === 'undefined' || !this.cache) return;

    try {
      if (this.cache.data?.country) {
        sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.cache));
      } else {
        // 接口未返回，说明不支持这个地区，默认为空
        sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify({
          country: '',
        }));
      }
    } catch (error) {
      console.warn('Failed to save IP location cache:', error);
    }
  }

  /**
   * 检查缓存是否有效
   */
  private isCacheValid(): boolean {
    if (!this.cache) return false;

    const now = Date.now();
    return now - this.cache.timestamp < this.cache.expiresIn;
  }

  /**
   * 获取缓存的 IP 位置信息
   */
  getCachedLocation(): LocationRes | null {
    if (this.isCacheValid()) {
      return this.cache!.data;
    }
    return null;
  }

  /**
   * 设置 IP 位置缓存
   */
  setLocation(location: LocationRes): void {
    this.cache = {
      data: location,
      timestamp: Date.now(),
      expiresIn: this.CACHE_DURATION
    };
    this.saveToStorage();
  }

  /**
   * 清除缓存
   */
  clearCache(): void {
    this.cache = null;
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(this.STORAGE_KEY);
    }
  }

  /**
   * 获取格式化的 IP 位置字符串，用于 X-IP-Location 头
   */
  getFormattedLocation(): LocationRes | null {
    const location = this.getCachedLocation();
    if (!location) return null;
    return location;
  }

  /**
   * 检查是否有有效的 IP 位置缓存
   */
  hasValidLocation(): boolean {
    return this.isCacheValid();
  }

  /**
   * 异步刷新 IP 位置信息（如果缓存失效）
   * 这个方法不会阻塞，会在后台更新缓存
   */
  async refreshLocationIfNeeded(): Promise<void> {
    // 如果缓存仍然有效，不需要刷新
    if (this.isCacheValid()) {
      return;
    }

    // 如果已经有刷新请求在进行中，等待它完成
    if (this.refreshPromise) {
      try {
        await this.refreshPromise;
      } catch (error) {
        console.warn('Background IP location refresh failed:', error);
      }
      return;
    }

    // 开始新的刷新请求
    this.refreshPromise = this.fetchLocationFromServer();

    try {
      const location = await this.refreshPromise;
      this.setLocation(location);
    } catch (error) {
      console.warn('Failed to refresh IP location:', error);
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * 从服务器获取 IP 位置信息（使用独立的 HTTP 客户端避免循环依赖）
   */
  private async fetchLocationFromServer(): Promise<LocationRes> {
    const response = await this.httpClient.get('/user/auth/v1/location');
    // 未上架地区访问; 显示禁止页面, apk不处理
    if (response.data?.code === ERROR_CODE_IP.IllegalRequestCode || !response.data?.data?.country) {
      if (typeof window !== 'undefined') {
        // 如果已经在 forbid 页面，不重复跳转
        if (window.location.pathname.includes('/forbid')) {
          return response.data?.data as LocationRes;
        }
        // 获取当前路径的 locale，并提取前缀
        const currentLocale = findLocale(window.location.pathname);
        const localePrefix = currentLocale.split('-')[0]; // 从 'en-US' 提取 'en'
        window.location.href = `/${localePrefix}/forbid`;
      }
    }
    return response.data?.data as LocationRes;
  }

  /**
   * 获取 IP 位置信息，如果缓存失效则异步刷新
   * 这个方法总是立即返回，不会阻塞
   */
  async getLocationWithBackgroundRefresh(): Promise<LocationRes | null> {
    const cached = this.getCachedLocation();

    // 如果缓存有效，直接返回
    if (cached) {
      return cached;
    }

    // 如果已经有请求在进行中，等待它完成
    if (this.refreshPromise) {
      try {
        const location = await this.refreshPromise;
        this.setLocation(location);
        return location;
      } catch (error) {
        console.warn('Failed to get IP location from pending request:', error);
        return null;
      }
    }

    // 开始新的请求并缓存promise，防止重复请求
    this.refreshPromise = this.fetchLocationFromServer();

    try {
      const location = await this.refreshPromise;
      this.setLocation(location);
      return location;
    } catch (error) {
      console.warn('Failed to fetch IP location:', error);
      return null;
    } finally {
      this.refreshPromise = null;
    }
  }
}

// 创建单例实例
export const ipLocationManager = new IpLocationManager();
